/**
 * Desktop IPC command registry. Every command is validated in the main process with the `input`
 * schema, checked against the cached entitlement (capability + account scope), then routed. The
 * envelope carries operationId and accountId (null for global commands).
 */
import { z } from 'zod';
import { rumiCodeCommands } from './rumi-code.js';
import {
  AccountIdSchema,
  AccountProviderSchema,
  AgendaIdSchema,
  ArtifactIdSchema,
  CanonicalKeySchema,
  ConnectionDraftIdSchema,
  ConnectionIdSchema,
  DatasetIdSchema,
  // notifications change
  NotificationIdSchema,
  OperationIdSchema,
  OutreachBatchIdSchema,
  ProviderSchema,
  RecordKeySchema,
  RumiSessionIdSchema,
  RumiTurnIdSchema,
  RunIdSchema,
  SnapshotIdSchema,
  TaskGroupIdSchema,
  TemplateIdSchema,
  WorkspaceIdSchema,
} from '../ids.js';
import type { Capability } from '../roles.js';
import { EmployeeCheckSchema, SyncJobStateSchema } from '../status.js';
import { ContentManifestSchema, ReleaseChannelSchema, SessionUserSchema } from '../api/entities.js';
// notifications change
import { NotificationActionSchema, NotificationCategorySchema, NotificationLevelSchema, NotificationSourceSchema } from '../api/entities.js';
// local-accounts change: registry browse rows come straight from the server contract.
import { RegistryAccountSchema } from '../api/accounts.js';
import { IsoDateTimeSchema, SemVerSchema } from '../api/common.js';
import {
  AgendaPreviewSchema,
  AgendaSchema,
  AgendaSettingsSchema,
  ArtifactSchema,
  ArtifactTypeSchema,
  ClassificationPatchSchema,
  ContextManifestSchema,
  CoverageManifestSchema,
  DesktopSettingsSchema,
  DisplayPrefsSchema,
  OutreachContactsSchema,
  EffectiveAskSchema,
  EmployeeComplianceRowSchema,
  GenerationProvenanceSchema,
  InfrastructureRowSchema,
  LayoutStateSchema,
  NoteSchema,
  NoteScopeSchema,
  OutreachBatchSchema,
  OutreachModeSchema,
  OverrideSchema,
  OverrideScopeSchema,
  QuarantinedRecipientSchema,
  RecipientSchema,
  RenderedMessageSchema,
  RuleSchema,
  RumiModeSchema,
  RumiScopeSchema,
  RumiSessionSchema,
  RumiTurnSchema,
  ScheduleListIdSchema,
  ScheduleStateSchema,
  SemanticDiffSchema,
  SnapshotDiffSchema,
  SnapshotSchema,
  SyncIssueSchema,
  SyncRunSchema,
  TaskGroupSchema,
  VulnerabilityDetailRowSchema,
  VulnerabilityEmptyReasonSchema,
  VulnerabilityFiltersSchema,
  VulnerabilitySummaryRowSchema,
} from '../providers/records.js';
import {
  ApiLogEntrySchema,
  AccountFrameworkSchema,
  AppStateSchema,
  AttachResultSchema,
  AuditOutboxStatusSchema,
  ConnectionTestResultSchema,
  ContentDiffSchema,
  ContentStateSchema,
  DeviceInfoSchema,
  DiagnosticEntrySchema,
  DiscoveryResultSchema,
  DocPageSchema,
  EmailTemplateViewSchema,
  EmployeeReportFiltersSchema,
  EntitlementSummarySchema,
  ExportManifestSchema,
  GoogleStatusSchema,
  LocalAccountSchema,
  LocalConnectionSchema,
  // notifications change
  LocalNotificationSchema,
  MigrationMappingSchema,
  MigrationPlanSchema,
  MigrationResultSchema,
  MissingAskQueueSchema,
  NoteSaveResultSchema,
  // notifications change
  NotificationCountsSchema,
  NotificationDedupeKeySchema,
  NotificationDismissResultSchema,
  NotificationMutationResultSchema,
  NotificationPullResultSchema,
  OutreachDuplicateWarningSchema,
  PublishResultSchema,
  RecoveryStateSchema,
  RetentionInfoSchema,
  RulePatchSchema,
  RuleUsageSchema,
  RuleViewSchema,
  RumiStatusSchema,
  RumiUsageSummarySchema,
  ScopePreviewSchema,
  SenderInfoSchema,
  SharedProposalSchema,
  SheetPublishResultSchema,
  SourceQueryResultSchema,
  SourceQuerySchema,
  SourceRecordDetailSchema,
  SourceRowSchema,
  StorageStatusSchema,
  SupportBundleManifestSchema,
  SupportBundleOptionsSchema,
  TelemetryPropsSchema,
  TelemetryUiEventSchema,
  TokenDiagnosticsSchema,
  UpdateCheckResultSchema,
  VulnerabilityCountsSchema,
} from './shapes.js';

const count = z.number().int().nonnegative();
const Empty = z.object({}).strict();
const Ok = z.object({ ok: z.literal(true) });
const isoDateTime = IsoDateTimeSchema;

/** `global`: accountId must be null. `account`: accountId required and must be assigned. `either`: optional. */
export type CommandScope = 'global' | 'account' | 'either';

export interface CommandDef<I extends z.ZodType = z.ZodType, O extends z.ZodType = z.ZodType> {
  input: I;
  output: O;
  scope: CommandScope;
  /** Capability required (any-of when an array). Absent = signed-in user with a valid entitlement. */
  capability?: Capability | readonly Capability[];
  /** Command may run while locked / signed out (ping, getState, signIn, layout). */
  allowLocked?: boolean;
  /** Requires a local reauthentication (credential reveal). */
  recentAuth?: boolean;
  /** Performs an external write; main persists intent + audit event first (BUILD_PLAN section 10). */
  externalWrite?: boolean;
  /** Only these commands may enter the job engine (A03). */
  startsCollection?: boolean;
  /**
   * local-accounts change: dev-only command. Main dispatches it only when `system.ping` mode is
   * `local`; elsewhere it fails with `unsupported` and the renderer hides its affordance.
   */
  localModeOnly?: boolean;
  description: string;
}

function cmd<I extends z.ZodType, O extends z.ZodType>(def: CommandDef<I, O>): CommandDef<I, O> {
  return def;
}

const accountRef = z.object({ accountId: AccountIdSchema });
const snapshotOpt = { snapshotId: SnapshotIdSchema.nullable().optional() };

export const COMMANDS = {
  ...rumiCodeCommands,
  // -------------------------------------------------------------------------------------------
  // system
  // -------------------------------------------------------------------------------------------
  'system.ping': cmd({
    input: Empty,
    output: z.object({ pong: z.literal(true), time: isoDateTime, appVersion: SemVerSchema, mode: z.enum(['local', 'cloud']), workerReady: z.boolean() }),
    scope: 'global',
    allowLocked: true,
    description: 'Liveness of main + worker.',
  }),
  'system.getState': cmd({ input: Empty, output: AppStateSchema, scope: 'global', allowLocked: true, description: 'Full renderer bootstrap state.' }),
  // Product analytics (PostHog, GET-13): the sandboxed renderer has no network, so UI events travel
  // over the bridge and main captures them with the desktop project key. Allowlisted names only.
  'telemetry.capture': cmd({
    input: z.object({ event: TelemetryUiEventSchema, props: TelemetryPropsSchema.optional() }),
    output: z.object({ accepted: z.boolean() }),
    scope: 'global',
    allowLocked: true,
    description: 'Report a renderer product-analytics event (allowlisted name, primitive props); main forwards it to PostHog.',
  }),
  // added by desktop-main fix pass (LH-03/F05): recovery screen actions (A21). retry restarts the
  // worker; reveal_backup shows the backup (or the database) in Finder; restore_backup replaces the
  // failed profile database with the pre-migration backup after moving the failed file aside.
  'system.recovery': cmd({
    input: z.object({ action: z.enum(['retry', 'reveal_backup', 'restore_backup']) }),
    output: z.object({ status: z.enum(['recovered', 'still_failing', 'revealed', 'unavailable']), recovery: RecoveryStateSchema.nullable() }),
    scope: 'global',
    allowLocked: true,
    description: 'Profile database recovery actions; only meaningful while the app phase is recovery.',
  }),
  'system.openExternal': cmd({
    input: z.object({ url: z.string().url() }),
    output: z.object({ opened: z.boolean(), reason: z.enum(['not_allowlisted', 'invalid']).nullable() }),
    scope: 'global',
    allowLocked: true,
    description: 'Open an allowlisted HTTPS URL (or sim localhost in local mode) in the default browser.',
  }),
  // appearance change: an interface size preset is applied as the window zoom factor by main.
  'system.setZoomFactor': cmd({ input: z.object({ factor: z.number().min(0.5).max(3) }), output: z.object({ factor: z.number() }), scope: 'global', allowLocked: true, description: 'Apply an interface size preset (Settings › Appearance) as the window zoom factor.' }),
  'system.copyToClipboard': cmd({
    input: z.object({ text: z.string().max(100_000), sensitive: z.boolean().default(false) }),
    output: Ok,
    scope: 'global',
    allowLocked: true,
    description: 'Copy text; sensitive values are cleared from the clipboard after 60s.',
  }),
  'system.getDocumentation': cmd({
    input: z.object({ slug: z.string().max(120).optional(), section: DocPageSchema.shape.section.optional() }),
    output: z.object({ items: z.array(DocPageSchema), contentVersion: SemVerSchema.nullable() }),
    scope: 'global',
    allowLocked: true,
    description: 'Documentation pages from the active content bundle.',
  }),
  'system.searchDocumentation': cmd({
    input: z.object({ query: z.string().min(1).max(200), limit: z.number().int().min(1).max(50).default(20) }),
    output: z.object({ results: z.array(z.object({ slug: z.string(), title: z.string(), section: DocPageSchema.shape.section, snippet: z.string(), score: z.number() })) }),
    scope: 'global',
    allowLocked: true,
    description: 'Full-text search over documentation.',
  }),
  'system.getUpdateState': cmd({ input: Empty, output: UpdateCheckResultSchema, scope: 'global', allowLocked: true, description: 'Read the current desktop update state without checking the network.' }),
  'system.checkForUpdate': cmd({ input: Empty, output: UpdateCheckResultSchema, scope: 'global', allowLocked: true, description: 'Check for and download the latest published ToDesktop release.' }),
  'system.restartForUpdate': cmd({ input: Empty, output: UpdateCheckResultSchema, scope: 'global', allowLocked: true, description: 'Checkpoint active work, then restart to install a downloaded update.' }),
  'system.exportSupportBundle': cmd({
    input: SupportBundleOptionsSchema.extend({ destinationPath: z.string().optional() }),
    output: z.object({ path: z.string(), bytes: count, manifest: SupportBundleManifestSchema }),
    scope: 'global',
    description: 'Write a sanitized support bundle to disk; emits diagnostics.exported.',
  }),
  'system.previewSupportBundle': cmd({ input: SupportBundleOptionsSchema, output: z.object({ manifest: SupportBundleManifestSchema }), scope: 'global', description: 'Preview what a support bundle would contain.' }),

  // -------------------------------------------------------------------------------------------
  // auth / device
  // -------------------------------------------------------------------------------------------
  'auth.signIn': cmd({
    input: Empty,
    output: z.object({ status: z.enum(['browser_opened', 'already_signed_in', 'offline']), loopbackPort: z.number().int().nullable(), expiresAt: isoDateTime.nullable() }),
    scope: 'global',
    allowLocked: true,
    description: 'Start OIDC PKCE in the system browser with a loopback callback. Result arrives via session.changed.',
  }),
  'auth.signOut': cmd({ input: z.object({ everywhere: z.boolean().default(false) }), output: Ok, scope: 'global', allowLocked: true, description: 'Sign out, clear session and transient credentials, cancel provider work safely.' }),
  'auth.getSession': cmd({
    input: Empty,
    output: z.object({ user: SessionUserSchema.nullable(), deviceId: DeviceInfoSchema.shape.deviceId, entitlement: EntitlementSummarySchema, sessionExpiresAt: isoDateTime.nullable() }),
    scope: 'global',
    allowLocked: true,
    description: 'Current session and entitlement summary.',
  }),
  'auth.renewEntitlement': cmd({ input: Empty, output: z.object({ entitlement: EntitlementSummarySchema }), scope: 'global', allowLocked: true, description: 'Force an entitlement renewal (also used after sleep/wake).' }),
  'device.getInfo': cmd({ input: Empty, output: DeviceInfoSchema, scope: 'global', allowLocked: true, description: 'Device identity and registration state.' }),

  // -------------------------------------------------------------------------------------------
  // content
  // -------------------------------------------------------------------------------------------
  'content.getActiveBundle': cmd({
    input: Empty,
    output: z.object({ state: ContentStateSchema, manifest: ContentManifestSchema.nullable(), pendingVersion: SemVerSchema.nullable() }),
    scope: 'global',
    allowLocked: true,
    description: 'Active bundle manifest and state.',
  }),
  'content.refresh': cmd({
    input: z.object({ channel: ReleaseChannelSchema.optional() }),
    output: z.object({ result: z.enum(['unchanged', 'downloaded', 'activated', 'rejected', 'offline']), version: SemVerSchema.nullable(), reason: z.string().nullable() }),
    scope: 'global',
    description: 'Check the manifest, download, verify signature + digest, activate. Never touches artifacts, drafts or overrides.',
  }),
  'content.adoptVersion': cmd({
    input: z.object({ version: SemVerSchema, agendaIds: z.array(AgendaIdSchema).default([]), adoptAllAgendas: z.boolean().default(false) }),
    output: z.object({ adoptedAgendaCount: count, version: SemVerSchema }),
    scope: 'global',
    description: 'Pin existing agendas to a newer template version after the diff was shown.',
  }),
  'content.diffVersions': cmd({ input: z.object({ fromVersion: SemVerSchema.nullable(), toVersion: SemVerSchema, types: z.array(ContentManifestSchema.shape.items.element.shape.type).optional() }), output: ContentDiffSchema, scope: 'global', description: 'Diff two locally retained bundle versions.' }),

  // -------------------------------------------------------------------------------------------
  // accounts (local view)
  // -------------------------------------------------------------------------------------------
  'accounts.list': cmd({ input: z.object({ includeArchived: z.boolean().default(false) }), output: z.object({ items: z.array(LocalAccountSchema), syncedAt: isoDateTime.nullable() }), scope: 'global', description: 'Local accounts (profile.db) with local state. syncedAt: last registry report delivery, if any.' }),
  'accounts.get': cmd({ input: accountRef, output: z.object({ account: LocalAccountSchema }), scope: 'account', description: 'One account with local state.' }),
  // local-accounts change: create locally first (id = registryAccountId ?? newId()), emit accounts.changed
  // {reason: 'created'}, audit account.created, enqueue a registry report. No server round trip blocks it.
  'accounts.create': cmd({
    input: z.object({
      displayName: z.string().min(1).max(120),
      provider: AccountProviderSchema,
      notes: z.string().max(2000).optional(),
      /** Reuse a registry row's stable id (origin `registry`); absent = client-generated id (origin `new`). */
      registryAccountId: AccountIdSchema.optional(),
    }),
    output: z.object({ account: LocalAccountSchema }),
    scope: 'global',
    description: 'Create an account in the local profile DB immediately and report it to the registry asynchronously.',
  }),
  /**
   * @deprecated local-accounts change: alias that delegates to `accounts.create` (origin `new`).
   * Callers should move to `accounts.create`; kept so existing renderer paths keep working.
   */
  'accounts.register': cmd({
    input: z.object({ displayName: z.string().min(1).max(120), provider: AccountProviderSchema, notes: z.string().max(2000).optional() }),
    output: z.object({ account: LocalAccountSchema }),
    scope: 'global',
    capability: 'account.register',
    description: 'Deprecated alias of accounts.create.',
  }),
  // local-accounts change: registry browse for the "From registry" tab. `available: false` when the server
  // is unreachable (items empty); `New account` keeps working regardless.
  'accounts.registryBrowse': cmd({
    input: z.object({ search: z.string().max(120).optional(), cursor: z.string().min(1).max(512).optional(), includeArchived: z.boolean().optional() }),
    output: z.object({ items: z.array(RegistryAccountSchema), nextCursor: z.string().nullable(), available: z.boolean() }),
    scope: 'global',
    description: 'Browse the server registry (search, cursor, archived filter); available=false when offline.',
  }),
  // local-accounts change: Settings -> Account & session "Registry reports: N pending · last delivered <time>".
  'accounts.registryStatus': cmd({
    input: Empty,
    output: z.object({ pendingReports: count, lastDeliveredAt: isoDateTime.nullable() }),
    scope: 'global',
    description: 'Registry report outbox status.',
  }),
  // real-providers change: `accounts.addSimulatorTenants` (dev-only fixture customers) is removed;
  // the product never creates simulated tenants. The `simulator` value of AccountOrigin stays for
  // schema compatibility with rows created before this change.
  'accounts.rename': cmd({ input: accountRef.extend({ displayName: z.string().min(1).max(120) }), output: z.object({ account: LocalAccountSchema }), scope: 'account', capability: 'account.register', description: 'Rename (server + local).' }),
  'accounts.setNotes': cmd({ input: accountRef.extend({ notes: z.string().max(2000) }), output: z.object({ account: LocalAccountSchema }), scope: 'account', description: 'Set the internal account note.' }),
  'accounts.archive': cmd({ input: accountRef, output: z.object({ account: LocalAccountSchema }), scope: 'account', capability: 'account.register', description: 'Archive on the server; local data retained.' }),
  'accounts.deleteLocal': cmd({
    input: accountRef.extend({ confirm: z.literal(true) }),
    output: z.object({ ok: z.literal(true), deletedBytes: count }),
    scope: 'account',
    description: 'Delete the local account store, credentials and history. Server registration untouched.',
  }),
  'accounts.setDisplayPrefs': cmd({ input: accountRef.extend({ prefs: DisplayPrefsSchema.partial() }), output: z.object({ account: LocalAccountSchema }), scope: 'account', description: 'Pin, short label, initials, retained colour preference, order.' }),
  // template feedback 2026-09-25: the customer's points of contact for outreach Cc (local only).
  'accounts.setOutreachContacts': cmd({ input: accountRef.extend({ contacts: OutreachContactsSchema }), output: z.object({ account: LocalAccountSchema }), scope: 'account', description: 'Customer contacts the analyst may Cc on employee outreach, and whether they are pre-selected.' }),

  // -------------------------------------------------------------------------------------------
  // connections
  // -------------------------------------------------------------------------------------------
  'connections.list': cmd({ input: accountRef, output: z.object({ items: z.array(LocalConnectionSchema) }), scope: 'account', description: 'Local connections for an account.' }),
  'connections.beginVanta': cmd({
    input: z.object({ clientId: z.string().min(1).max(256), clientSecret: z.string().min(1).max(1024), apiBaseUrl: z.string().url().optional(), browserUrl: z.string().url().optional() }),
    output: DiscoveryResultSchema,
    scope: 'global',
    capability: 'account.connect',
    description: 'Mint a token (under lease), discover tenant identity and capabilities; returns a draft. Secret held in main only.',
  }),
  'connections.beginDrata': cmd({
    // real-providers change: `region` selects the verified regional host (US default); ignored while the adapters point at the simulator.
    input: z.object({ apiKey: z.string().min(1).max(1024), apiBaseUrl: z.string().url().optional(), region: z.enum(['us', 'eu', 'apac']).optional() }),
    output: DiscoveryResultSchema,
    scope: 'global',
    capability: 'account.connect',
    description: 'Discover Drata workspaces and capabilities; returns a draft.',
  }),
  'connections.discover': cmd({ input: z.object({ draftId: ConnectionDraftIdSchema }), output: DiscoveryResultSchema, scope: 'global', capability: 'account.connect', description: 'Re-run discovery for an existing draft.' }),
  'connections.attach': cmd({
    input: accountRef.extend({ draftId: ConnectionDraftIdSchema, workspaceId: WorkspaceIdSchema.optional(), replaceConnectionId: ConnectionIdSchema.optional(), acceptTenant: z.boolean().default(false) }),
    output: AttachResultSchema,
    scope: 'account',
    capability: 'account.connect',
    description: 'Attach a discovered draft to an account after the tenant-mismatch guard; registers metadata on the server.',
  }),
  'connections.test': cmd({ input: z.object({ connectionId: ConnectionIdSchema }), output: ConnectionTestResultSchema, scope: 'account', capability: 'account.connect', startsCollection: true, description: 'Connect and Test: credential, identity, scope and dataset probes (bounded reads).' }),
  'connections.replaceCredentials': cmd({ input: z.object({ connectionId: ConnectionIdSchema, draftId: ConnectionDraftIdSchema }), output: z.object({ connection: LocalConnectionSchema }), scope: 'account', capability: 'account.connect', description: 'Replace credentials keeping the connection id and local state (tenant must match).' }),
  'connections.reconnect': cmd({ input: z.object({ connectionId: ConnectionIdSchema }), output: z.object({ connection: LocalConnectionSchema, health: LocalConnectionSchema.shape.health }), scope: 'account', capability: 'account.connect', description: 'Re-authorize with stored credentials (token renewal under lease).' }),
  'connections.remove': cmd({ input: z.object({ connectionId: ConnectionIdSchema, deleteLocalData: z.boolean().default(false) }), output: Ok, scope: 'account', capability: 'account.connect', description: 'Remove a connection and its credential; optionally its snapshots.' }),
  'connections.tokenDiagnostics': cmd({
    input: z.object({ connectionId: ConnectionIdSchema, reveal: z.boolean().default(false) }),
    output: TokenDiagnosticsSchema,
    scope: 'account',
    capability: ['account.connect', 'credential.debug'],
    recentAuth: true,
    description: 'Token expiry, scopes and health. reveal requires credential.debug + local reauth; only the access is audited.',
  }),

  // -------------------------------------------------------------------------------------------
  // sync
  // -------------------------------------------------------------------------------------------
  'sync.preflight': cmd({ input: z.object({ connectionId: ConnectionIdSchema, datasetIds: z.array(DatasetIdSchema).optional() }), output: ScopePreviewSchema, scope: 'account', capability: 'grc.sync', description: 'Scope preview before Sync Now (no provider reads).' }),
  'sync.start': cmd({ input: z.object({ connectionId: ConnectionIdSchema, datasetIds: z.array(DatasetIdSchema).min(1) }), output: z.object({ runId: RunIdSchema, run: SyncRunSchema }), scope: 'account', capability: 'grc.sync', startsCollection: true, description: 'Sync Now. One active job per connection.' }),
  'sync.cancel': cmd({ input: z.object({ runId: RunIdSchema }), output: z.object({ run: SyncRunSchema }), scope: 'account', capability: 'grc.sync', description: 'Cancel: stop queued requests, let the current safe checkpoint finish.' }),
  'sync.resume': cmd({ input: z.object({ runId: RunIdSchema }), output: z.object({ runId: RunIdSchema, run: SyncRunSchema }), scope: 'account', capability: 'grc.sync', startsCollection: true, description: 'Resume a paused/interrupted run from its checkpoints.' }),
  'sync.retryFailed': cmd({ input: z.object({ runId: RunIdSchema }), output: z.object({ runId: RunIdSchema, run: SyncRunSchema }), scope: 'account', capability: 'grc.sync', startsCollection: true, description: 'Retry only the failed datasets/pages of a partial run.' }),
  'sync.listRuns': cmd({
    input: z.object({ accountId: AccountIdSchema.optional(), connectionId: ConnectionIdSchema.optional(), states: z.array(SyncJobStateSchema).optional(), limit: z.number().int().min(1).max(200).default(50), cursor: z.string().optional() }),
    output: z.object({ items: z.array(SyncRunSchema), nextCursor: z.string().nullable() }),
    scope: 'either',
    description: 'Runs (jobs panel: all accounts; sync history: one account).',
  }),
  'sync.getRun': cmd({ input: z.object({ runId: RunIdSchema }), output: z.object({ run: SyncRunSchema }), scope: 'account', description: 'One run with datasets and transitions.' }),
  'sync.getIssues': cmd({ input: z.object({ runId: RunIdSchema, includeResolved: z.boolean().default(false) }), output: z.object({ items: z.array(SyncIssueSchema) }), scope: 'account', description: 'Structured issues for a run.' }),

  // -------------------------------------------------------------------------------------------
  // snapshots
  // -------------------------------------------------------------------------------------------
  'snapshots.list': cmd({ input: accountRef.extend({ connectionId: ConnectionIdSchema.optional() }), output: z.object({ items: z.array(SnapshotSchema), currentSnapshotId: SnapshotIdSchema.nullable(), selectedSnapshotId: SnapshotIdSchema.nullable() }), scope: 'account', description: 'Retained snapshots, newest first.' }),
  'snapshots.get': cmd({ input: z.object({ snapshotId: SnapshotIdSchema }), output: z.object({ snapshot: SnapshotSchema }), scope: 'account', description: 'One snapshot.' }),
  'snapshots.select': cmd({ input: accountRef.extend({ snapshotId: SnapshotIdSchema.nullable() }), output: z.object({ selectedSnapshotId: SnapshotIdSchema.nullable(), snapshot: SnapshotSchema.nullable() }), scope: 'account', description: 'Use historical snapshot (view only; null = current).' }),
  'snapshots.compare': cmd({ input: z.object({ fromSnapshotId: SnapshotIdSchema, toSnapshotId: SnapshotIdSchema, datasetIds: z.array(DatasetIdSchema).optional(), limit: z.number().int().min(1).max(5000).default(1000) }), output: SnapshotDiffSchema, scope: 'account', description: 'Added/removed/changed between two snapshots.' }),
  'snapshots.coverage': cmd({ input: accountRef.extend(snapshotOpt), output: CoverageManifestSchema, scope: 'account', description: 'Per-dataset coverage for the selected snapshot.' }),

  // -------------------------------------------------------------------------------------------
  // sources
  // -------------------------------------------------------------------------------------------
  'sources.query': cmd({ input: SourceQuerySchema, output: SourceQueryResultSchema, scope: 'account', description: 'Source explorer table query.' }),
  'sources.getRecord': cmd({ input: accountRef.extend({ recordKey: RecordKeySchema, ...snapshotOpt }), output: SourceRecordDetailSchema, scope: 'account', description: 'Inspector detail for one record.' }),
  'sources.getChildren': cmd({
    input: accountRef.extend({ recordKey: RecordKeySchema, ...snapshotOpt, showOk: z.boolean().default(true), page: count.default(0), pageSize: z.number().int().min(10).max(500).default(100) }),
    output: z.object({ rows: z.array(SourceRowSchema), total: count }),
    scope: 'account',
    description: 'Children of a parent record.',
  }),
  'sources.setInclusion': cmd({ input: accountRef.extend({ recordKeys: z.array(RecordKeySchema).min(1).max(1000), included: z.boolean().nullable() }), output: z.object({ overrides: z.array(OverrideSchema) }), scope: 'account', description: 'Include/exclude records from asks (null clears the override).' }),
  'sources.setIrrelevant': cmd({ input: accountRef.extend({ recordKeys: z.array(RecordKeySchema).min(1).max(1000), irrelevant: z.boolean().nullable() }), output: z.object({ overrides: z.array(OverrideSchema) }), scope: 'account', description: 'Mark records irrelevant (null clears).' }),
  'sources.setClassification': cmd({ input: accountRef.extend({ recordKey: RecordKeySchema, scope: OverrideScopeSchema, classification: ClassificationPatchSchema }), output: z.object({ override: OverrideSchema, effectiveAsk: EffectiveAskSchema }), scope: 'account', description: 'Edit classification flags at account or library scope.' }),
  'sources.setRemediationText': cmd({ input: accountRef.extend({ recordKey: RecordKeySchema, scope: OverrideScopeSchema, text: z.string().max(20_000).nullable() }), output: z.object({ override: OverrideSchema, effectiveAsk: EffectiveAskSchema }), scope: 'account', description: 'Edit remediation ask text at account or library scope.' }),
  'sources.batchMarkCustomAskNeeded': cmd({ input: accountRef.extend({ recordKeys: z.array(RecordKeySchema).min(1).max(5000), customAskNeeded: z.boolean() }), output: z.object({ updated: count }), scope: 'account', description: 'Batch-mark visible empty asks as needing a custom ask.' }),
  'sources.missingAskQueue': cmd({ input: accountRef.extend({ ...snapshotOpt, page: count.default(0), pageSize: z.number().int().min(10).max(500).default(50) }), output: MissingAskQueueSchema, scope: 'account', description: 'Missing-ask review queue with progress.' }),
  'sources.exportJson': cmd({
    input: SourceQuerySchema.omit({ page: true, pageSize: true }).extend({ destinationPath: z.string().optional() }),
    output: z.object({ path: z.string(), manifest: ExportManifestSchema, artifact: ArtifactSchema }),
    scope: 'account',
    capability: 'artifact.export',
    description: 'Export the filtered records as JSON with a manifest.',
  }),

  // -------------------------------------------------------------------------------------------
  // rules (shared master tests + personal library)
  // -------------------------------------------------------------------------------------------
  'rules.list': cmd({
    input: z.object({ provider: ProviderSchema.optional(), search: z.string().max(200).default(''), source: z.enum(['published', 'library', 'all']).default('all'), overriddenOnly: z.boolean().default(false), page: count.default(0), pageSize: z.number().int().min(10).max(500).default(100) }),
    output: z.object({ items: z.array(RuleViewSchema), total: count }),
    scope: 'global',
    description: 'Master tests and classification rules.',
  }),
  'rules.get': cmd({ input: z.object({ canonicalKey: CanonicalKeySchema }), output: z.object({ rule: RuleViewSchema, published: RuleSchema.nullable(), libraryOverride: OverrideSchema.nullable() }), scope: 'global', description: 'One rule with its layers.' }),
  'rules.update': cmd({ input: z.object({ canonicalKey: CanonicalKeySchema, patch: RulePatchSchema }), output: z.object({ rule: RuleViewSchema, override: OverrideSchema }), scope: 'global', description: 'Edit the personal library override for a rule.' }),
  'rules.suggestAsk': cmd({
    input: z.object({ canonicalKey: CanonicalKeySchema }),
    output: z.object({
      text: z.string(),
      source: z.enum(['remediation', 'description']),
      model: z.string(),
      inputTokens: count,
      outputTokens: count,
      estimatedCostMicroUsd: count,
    }),
    scope: 'global',
    capability: 'rumi.query',
    description: "Draft an ask for a rule from the provider's default text with the Agency wording guide (OpenAI through the assigned key; the draft is returned, never saved).",
  }),
  'rules.usage': cmd({ input: z.object({ canonicalKey: CanonicalKeySchema }), output: RuleUsageSchema, scope: 'global', description: 'Which local accounts use a rule.' }),
  'rules.proposeShared': cmd({ input: z.object({ canonicalKeys: z.array(CanonicalKeySchema).min(1).max(500), summary: z.string().min(1).max(1000) }), output: SharedProposalSchema, scope: 'global', capability: 'template.edit', description: 'Export a reviewed proposal (diff + affected rules) for publication in the admin console.' }),

  // -------------------------------------------------------------------------------------------
  // agendas
  // -------------------------------------------------------------------------------------------
  'frameworks.listForAccount': cmd({ input: accountRef, output: z.array(AccountFrameworkSchema), scope: 'account', description: 'Framework activity and agenda exclusions from the current local snapshot.' }),
  'frameworks.setExclusions': cmd({ input: accountRef.extend({ frameworkIds: z.array(z.string().min(1)) }), output: Ok, scope: 'account', description: 'Replace the Drata agenda exclusions for this client.' }),
  'agendas.list': cmd({ input: accountRef, output: z.object({ items: z.array(AgendaSchema) }), scope: 'account', description: 'Agendas for an account.' }),
  'agendas.create': cmd({
    input: accountRef.extend({ title: z.string().min(1).max(200), meetingSeries: z.string().max(120).optional(), templateId: TemplateIdSchema.optional(), isDefault: z.boolean().default(false), settings: AgendaSettingsSchema.partial().optional() }),
    output: z.object({ agenda: AgendaSchema }),
    scope: 'account',
    capability: 'artifact.create',
    description: 'Create a local agenda (no Google write yet).',
  }),
  'agendas.get': cmd({ input: z.object({ agendaId: AgendaIdSchema }), output: z.object({ agenda: AgendaSchema, artifacts: z.array(ArtifactSchema) }), scope: 'account', description: 'One agenda with its publication history.' }),
  'agendas.preview': cmd({ input: z.object({ agendaId: AgendaIdSchema, ...snapshotOpt, meetingDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(), settings: AgendaSettingsSchema.partial().optional(), partialDataAcknowledged: z.boolean().default(false) }), output: AgendaPreviewSchema.extend({ previewDigest: z.string() }), scope: 'account', description: 'Render the agenda against a snapshot; no provider or Google call.' }),
  'agendas.diff': cmd({ input: z.object({ agendaId: AgendaIdSchema, ...snapshotOpt, previewDigest: z.string().optional() }), output: SemanticDiffSchema, scope: 'account', description: 'Fetch the current document and compute a semantic diff against the preview.' }),
  'agendas.publish': cmd({
    input: z.object({ agendaId: AgendaIdSchema, ...snapshotOpt, previewDigest: z.string(), mode: z.enum(['update', 'create', 'save_as_new']), folderId: z.string().optional(), acknowledgeManualEdits: z.boolean().default(false), /** Asks without ask text are published as their fallback line only once acknowledged (template feedback 2026-09-25). */ acknowledgeMissingAsks: z.boolean().default(false), expectedRevisionId: z.string().nullable().optional() }),
    output: PublishResultSchema,
    scope: 'account',
    capability: 'artifact.create',
    externalWrite: true,
    description: 'Revision-aware write to Google Docs in recorded stages (create, content, formatting, links).',
  }),
  'agendas.unlink': cmd({ input: z.object({ agendaId: AgendaIdSchema }), output: z.object({ agenda: AgendaSchema }), scope: 'account', description: 'Unlink locally; remote file untouched.' }),
  'agendas.setDefault': cmd({ input: z.object({ agendaId: AgendaIdSchema }), output: z.object({ agenda: AgendaSchema }), scope: 'account', description: 'Mark as the default agenda.' }),
  'agendas.repair': cmd({
    input: z.object({ agendaId: AgendaIdSchema, strategy: z.enum(['recreate_markers', 'reformat', 'relink', 'rewrite_document', 'add_tabs']), documentId: z.string().optional() }),
    output: z.object({ agenda: AgendaSchema, stageResults: ArtifactSchema.shape.stageResults, warnings: z.array(z.string()) }),
    scope: 'account',
    capability: 'artifact.create',
    externalWrite: true,
    description: 'Repair damaged markers/formatting or relink to a document; never creates a duplicate.',
  }),
  'agendas.remoteDelete': cmd({
    input: z.object({ agendaId: AgendaIdSchema, confirmDocumentIds: z.array(z.string()).min(1) }),
    output: z.object({ deleted: z.array(z.string()), failed: z.array(z.object({ documentId: z.string(), reason: z.string() })) }),
    scope: 'account',
    capability: 'artifact.create',
    externalWrite: true,
    description: 'Trash the listed remote documents (explicit, shows every affected document).',
  }),

  // -------------------------------------------------------------------------------------------
  // reports
  // -------------------------------------------------------------------------------------------
  'reports.infrastructurePreview': cmd({
    input: accountRef.extend({ ...snapshotOpt, partialDataAcknowledged: z.boolean().default(false) }),
    output: z.object({ rows: z.array(InfrastructureRowSchema), retiredRows: z.array(InfrastructureRowSchema), provenance: GenerationProvenanceSchema, warnings: z.array(z.string()), existingArtifact: ArtifactSchema.nullable(), previewDigest: z.string() }),
    scope: 'account',
    description: 'Infrastructure report rows merged with human-owned Status / Notes from the linked sheet.',
  }),
  'reports.infrastructurePublish': cmd({ input: accountRef.extend({ ...snapshotOpt, previewDigest: z.string(), artifactId: ArtifactIdSchema.optional(), title: z.string().max(200).optional(), /** Publish although the linked sheet could not be read and human values came from the local copy (added by desktop-worker fix pass, R8). */ acknowledgeLocalMerge: z.boolean().optional() }), output: SheetPublishResultSchema, scope: 'account', capability: 'artifact.create', externalWrite: true, description: 'Create/update the Infrastructure Sheet merging by hidden stable key.' }),
  'reports.employeeCompliancePreview': cmd({
    input: accountRef.extend({ ...snapshotOpt, filters: EmployeeReportFiltersSchema.default({ includeExcluded: false, failedOnly: false }), partialDataAcknowledged: z.boolean().default(false) }),
    output: z.object({ rows: z.array(EmployeeComplianceRowSchema), columns: z.array(EmployeeCheckSchema), unsupportedChecks: z.array(EmployeeCheckSchema), provenance: GenerationProvenanceSchema, warnings: z.array(z.string()), outreachNote: z.string(), previewDigest: z.string() }),
    scope: 'account',
    description: 'Employee Compliance rows with distinct PASS/FAIL/DUE SOON/MISCONFIGURED/EXCLUDED/UNKNOWN/UNSUPPORTED.',
  }),
  'reports.employeeCompliancePublish': cmd({ input: accountRef.extend({ ...snapshotOpt, previewDigest: z.string(), artifactId: ArtifactIdSchema.optional(), title: z.string().max(200).optional(), /** Filters the preview was generated with; the digest binds to them. added by desktop-e2e */ filters: EmployeeReportFiltersSchema.optional() }), output: SheetPublishResultSchema, scope: 'account', capability: 'artifact.create', externalWrite: true, description: 'Create/update the Employee Compliance Sheet.' }),
  'reports.vulnerabilitiesPreview': cmd({
    input: accountRef.extend({ ...snapshotOpt, filters: VulnerabilityFiltersSchema.default({ includeNonFixable: false, includeUnknownFixability: false }) }),
    output: z.object({ summaryRows: z.array(VulnerabilitySummaryRowSchema), detailRows: z.array(VulnerabilityDetailRowSchema), emptyReason: VulnerabilityEmptyReasonSchema, counts: VulnerabilityCountsSchema, provenance: GenerationProvenanceSchema, warnings: z.array(z.string()), previewDigest: z.string() }),
    scope: 'account',
    description: 'In-app vulnerability report (summary + detail) from the selected snapshot.',
  }),
  'reports.vulnerabilitiesExportCsv': cmd({ input: accountRef.extend({ ...snapshotOpt, filters: VulnerabilityFiltersSchema, destinationPath: z.string().optional() }), output: z.object({ path: z.string(), artifact: ArtifactSchema, rowCount: count }), scope: 'account', capability: 'artifact.export', description: 'Local CSV export (summary + detail files).' }),
  'reports.vulnerabilitiesExportJson': cmd({ input: accountRef.extend({ ...snapshotOpt, filters: VulnerabilityFiltersSchema, destinationPath: z.string().optional() }), output: z.object({ path: z.string(), artifact: ArtifactSchema, rowCount: count }), scope: 'account', capability: 'artifact.export', description: 'Local JSON export with provenance.' }),
  'reports.vulnerabilitiesPublishSheet': cmd({ input: accountRef.extend({ ...snapshotOpt, filters: VulnerabilityFiltersSchema, previewDigest: z.string(), artifactId: ArtifactIdSchema.optional(), title: z.string().max(200).optional() }), output: SheetPublishResultSchema, scope: 'account', capability: 'artifact.create', externalWrite: true, description: 'Create/update the vulnerability Google Sheet (summary + detail tabs).' }),
  'reports.listArtifacts': cmd({ input: accountRef.extend({ types: z.array(ArtifactTypeSchema).optional() }), output: z.object({ items: z.array(ArtifactSchema) }), scope: 'account', description: 'Artifacts and links for an account.' }),
  // added by desktop-worker fix pass (R3): unlink / relink / remote delete for linked sheets, mirroring agendas.*
  'reports.unlinkArtifact': cmd({ input: accountRef.extend({ artifactId: ArtifactIdSchema }), output: z.object({ artifact: ArtifactSchema }), scope: 'account', capability: 'artifact.create', description: 'Unlink a sheet artifact locally; the remote file is untouched.' }),
  'reports.relinkArtifact': cmd({ input: accountRef.extend({ artifactId: ArtifactIdSchema, spreadsheetId: z.string().min(1).max(256) }), output: z.object({ artifact: ArtifactSchema }), scope: 'account', capability: 'artifact.create', description: 'Point a sheet artifact at another spreadsheet the operator owns; never creates a duplicate.' }),
  'reports.remoteDeleteArtifact': cmd({
    input: accountRef.extend({ artifactId: ArtifactIdSchema, confirmSpreadsheetId: z.string().min(1).max(256) }),
    output: z.object({ artifact: ArtifactSchema, deleted: z.boolean() }),
    scope: 'account',
    capability: 'artifact.create',
    externalWrite: true,
    description: 'Trash the linked spreadsheet (explicit; the id must be confirmed).',
  }),

  // -------------------------------------------------------------------------------------------
  // outreach
  // -------------------------------------------------------------------------------------------
  'outreach.taskGroups': cmd({
    input: accountRef.extend(snapshotOpt),
    output: z.object({ items: z.array(TaskGroupSchema), snapshotId: SnapshotIdSchema.nullable(), snapshotAt: isoDateTime.nullable(), snapshotAgeHours: z.number().nullable(), freshnessOk: z.boolean(), blockedReason: z.enum(['no_snapshot', 'personnel_incomplete', 'recipient_mapping_incomplete', 'evidence_incomplete', 'unsupported_provider']).nullable() }),
    scope: 'account',
    capability: 'email.draft',
    description: 'Task groups with status counts and affected people.',
  }),
  'outreach.recipients': cmd({
    input: accountRef.extend({ taskGroupId: TaskGroupIdSchema, ...snapshotOpt, includeFutureHires: z.boolean().default(false), includeContractors: z.boolean().default(false) }),
    output: z.object({ eligible: z.array(RecipientSchema), quarantined: z.array(QuarantinedRecipientSchema), snapshotAt: isoDateTime.nullable() }),
    scope: 'account',
    capability: 'email.draft',
    description: 'Eligible recipients (identity ∩ failing entities ∩ device ownership) and quarantined identities.',
  }),
  'outreach.templates': cmd({ input: z.object({ category: TaskGroupSchema.shape.category.optional() }), output: z.object({ items: z.array(EmailTemplateViewSchema) }), scope: 'global', description: 'Email templates from the active bundle.' }),
  'outreach.renderPreview': cmd({
    input: accountRef.extend({ taskGroupId: TaskGroupIdSchema, mode: OutreachModeSchema, templateId: TemplateIdSchema, recipientPersonKeys: z.array(RecordKeySchema).min(1), overrides: z.object({ subject: z.string().max(300).optional(), body: z.string().max(20_000).optional(), /** {analyst_name} (template feedback 2026-09-25); defaults to the connected identity's first name. */ analystName: z.string().max(80).optional() }).default({}), /** Customer contacts to Cc (template feedback 2026-09-25); validated against the snapshot's people. */ cc: z.array(z.string().email().max(254)).max(10).default([]), ...snapshotOpt, /** Recipient policy (added by desktop-worker fix pass, OR-11). */ includeContractors: z.boolean().optional(), includeFutureHires: z.boolean().optional() }),
    output: z.object({ messages: z.array(RenderedMessageSchema), unresolvedVariables: z.array(z.string()), privacyWarnings: z.array(z.string()), sender: SenderInfoSchema, previewDigest: z.string(), duplicateWarning: OutreachDuplicateWarningSchema.nullable(), snapshotAgeHours: z.number().nullable(), freshnessOk: z.boolean() }),
    scope: 'account',
    capability: 'email.draft',
    description: 'Fully rendered messages (To = the connected identity in group mode, Bcc = employees, Cc = customer contacts); flags {employee_list} in Bcc mode (A16). Former, offboarding and deactivated people are never recipients.',
  }),
  'outreach.createDrafts': cmd({
    input: accountRef.extend({ taskGroupId: TaskGroupIdSchema, mode: OutreachModeSchema, templateId: TemplateIdSchema, recipientPersonKeys: z.array(RecordKeySchema).min(1), overrides: z.object({ subject: z.string().max(300).optional(), body: z.string().max(20_000).optional(), analystName: z.string().max(80).optional() }).default({}), cc: z.array(z.string().email().max(254)).max(10).default([]), ...snapshotOpt, previewDigest: z.string(), acknowledgeDuplicate: z.boolean().default(false), includeContractors: z.boolean().optional(), includeFutureHires: z.boolean().optional(), /** real-providers change: the 24h freshness rule gates draft creation; a recorded reason is the audited exception. */ staleReason: z.string().max(500).optional() }),
    output: z.object({ batch: OutreachBatchSchema }),
    scope: 'account',
    capability: 'email.draft',
    externalWrite: true,
    description: 'Create Gmail drafts (intent persisted first) and record draft ids. Blocked by the 24h freshness rule unless a reason is recorded.',
  }),
  // real-providers change: `outreach.send` removed. Agency creates Gmail drafts only; the operator
  // reviews and sends each one from Gmail.
  'outreach.listBatches': cmd({ input: accountRef.extend({ taskGroupId: TaskGroupIdSchema.optional() }), output: z.object({ items: z.array(OutreachBatchSchema) }), scope: 'account', description: 'Outreach batches for an account.' }),
  'outreach.getBatch': cmd({ input: z.object({ batchId: OutreachBatchIdSchema }), output: z.object({ batch: OutreachBatchSchema }), scope: 'account', description: 'One batch with item states.' }),
  // real-providers change: `outreach.reconcile` removed with the send path (no send, no outcome-unknown).

  // -------------------------------------------------------------------------------------------
  // google
  // -------------------------------------------------------------------------------------------
  'google.status': cmd({ input: Empty, output: GoogleStatusSchema, scope: 'global', description: 'Google OAuth connection state.' }),
  // changed by desktop-main fix pass (SEC-06): no caller-supplied scopes; main requests exactly its
  // configured minimal scope set (spec section 14). Proceeds when the stored grant needs re-auth.
  // real-google change: `not_configured` until an administrator stores the desktop OAuth client.
  'google.claimConnectReminder': cmd({ input: Empty, output: z.object({ show: z.boolean() }), scope: 'global', description: 'Show the optional Google connection reminder once per app launch, after sign-in.' }),
  'google.connect': cmd({ input: Empty, output: z.object({ status: z.enum(['browser_opened', 'already_connected', 'offline', 'not_configured']) }), scope: 'global', description: 'Start Google OAuth (PKCE, loopback) in the system browser with the fixed scope set. Result via google.changed.' }),
  'google.disconnect': cmd({ input: z.object({ revoke: z.boolean().default(true) }), output: Ok, scope: 'global', description: 'Forget (and optionally revoke) the Google grant.' }),
  'google.switchIdentity': cmd({ input: Empty, output: z.object({ status: z.enum(['browser_opened', 'offline', 'not_configured']) }), scope: 'global', description: 'Re-run OAuth with account selection; customer identity unchanged.' }),

  // -------------------------------------------------------------------------------------------
  // rumi
  // -------------------------------------------------------------------------------------------
  'rumi.status': cmd({ input: accountRef, output: RumiStatusSchema, scope: 'account', capability: 'rumi.query', description: 'Assignment readiness, model, policy, per-account disablement.' }),
  'rumi.listSessions': cmd({ input: accountRef, output: z.object({ items: z.array(RumiSessionSchema) }), scope: 'account', capability: 'rumi.query', description: 'Sessions for an account.' }),
  'rumi.createSession': cmd({ input: accountRef.extend({ title: z.string().max(200).optional(), ...snapshotOpt, defaultMode: RumiModeSchema.default('focused') }), output: z.object({ session: RumiSessionSchema }), scope: 'account', capability: 'rumi.query', description: 'New session bound to one account.' }),
  'rumi.deleteSession': cmd({ input: z.object({ sessionId: RumiSessionIdSchema }), output: Ok, scope: 'account', capability: 'rumi.query', description: 'Delete a session and its turns.' }),
  // added by Rumi composer: prepare context for the same model that will receive the question.
  'rumi.getContextManifest': cmd({ input: accountRef.extend({ sessionId: RumiSessionIdSchema, question: z.string().min(1).max(20_000), mode: RumiModeSchema, scope: RumiScopeSchema, useHigherCapabilityModel: z.boolean().default(false) }), output: ContextManifestSchema, scope: 'account', capability: 'rumi.query', description: 'What would be sent: destination, snapshot, categories, estimated tokens and cost.' }),
  'rumi.ask': cmd({
    input: z.object({ sessionId: RumiSessionIdSchema, question: z.string().min(1).max(20_000), mode: RumiModeSchema, scope: RumiScopeSchema, acknowledgedManifestDigest: z.string(), useHigherCapabilityModel: z.boolean().default(false) }),
    output: z.object({ operationId: OperationIdSchema, turnId: RumiTurnIdSchema }),
    scope: 'account',
    capability: 'rumi.query',
    externalWrite: true,
    description: 'Start a streamed Responses request via credential grant; deltas via rumi.* events.',
  }),
  'rumi.cancel': cmd({ input: z.object({ operationId: OperationIdSchema }), output: z.object({ cancelled: z.boolean(), turnId: RumiTurnIdSchema.nullable() }), scope: 'account', capability: 'rumi.query', description: 'Cancel an in-flight request; usage may still be incurred.' }),
  'rumi.listTurns': cmd({ input: z.object({ sessionId: RumiSessionIdSchema }), output: z.object({ items: z.array(RumiTurnSchema) }), scope: 'account', capability: 'rumi.query', description: 'Turns of a session.' }),
  'rumi.usageSummary': cmd({ input: z.object({ accountId: AccountIdSchema.optional(), from: isoDateTime.optional(), to: isoDateTime.optional() }), output: RumiUsageSummarySchema, scope: 'either', capability: 'rumi.query', description: 'Usage receipt totals and budget.' }),

  // -------------------------------------------------------------------------------------------
  // notes / schedule / layout / settings
  // -------------------------------------------------------------------------------------------
  'notes.get': cmd({ input: z.object({ scope: NoteScopeSchema, accountId: AccountIdSchema.optional() }), output: NoteSchema, scope: 'either', description: 'Global scratchpad or account note.' }),
  'notes.save': cmd({ input: z.object({ scope: NoteScopeSchema, accountId: AccountIdSchema.optional(), content: z.string().max(2_000_000), baseRevision: count }), output: NoteSaveResultSchema, scope: 'either', allowLocked: true, description: 'Autosave with optimistic revision check.' }),
  'notes.search': cmd({ input: z.object({ scope: NoteScopeSchema, accountId: AccountIdSchema.optional(), query: z.string().min(1).max(200) }), output: z.object({ matches: z.array(z.object({ line: count, column: count, length: count, preview: z.string() })), total: count }), scope: 'either', description: 'Find matches for next/previous navigation.' }),
  'schedule.get': cmd({ input: Empty, output: ScheduleStateSchema, scope: 'global', description: 'Priority and weekday lists.' }),
  'schedule.setOrder': cmd({ input: z.object({ listId: ScheduleListIdSchema, accountIds: z.array(AccountIdSchema) }), output: ScheduleStateSchema, scope: 'global', description: 'Reorder one list.' }),
  'schedule.move': cmd({ input: z.object({ accountId: AccountIdSchema, fromListId: ScheduleListIdSchema.nullable(), toListId: ScheduleListIdSchema, index: count }), output: ScheduleStateSchema, scope: 'global', description: 'Drag an account into/within a list (personal work list, not a scheduled sync).' }),
  'schedule.setDone': cmd({ input: z.object({ listId: ScheduleListIdSchema, accountId: AccountIdSchema, done: z.boolean() }), output: ScheduleStateSchema, scope: 'global', description: 'Toggle a done marker.' }),
  'schedule.resetDone': cmd({ input: z.object({ listId: ScheduleListIdSchema.nullable() }), output: ScheduleStateSchema, scope: 'global', description: 'Reset done markers for one list or all.' }),
  'schedule.setLastSelected': cmd({ input: z.object({ accountId: AccountIdSchema.nullable() }), output: ScheduleStateSchema, scope: 'global', description: 'Remember the last selected account.' }),
  'layout.get': cmd({ input: Empty, output: LayoutStateSchema, scope: 'global', allowLocked: true, description: 'Persisted layout.' }),
  'layout.set': cmd({ input: LayoutStateSchema.partial(), output: LayoutStateSchema, scope: 'global', allowLocked: true, description: 'Persist layout changes (debounced by the renderer).' }),
  'settings.get': cmd({ input: Empty, output: DesktopSettingsSchema, scope: 'global', allowLocked: true, description: 'Desktop settings.' }),
  'settings.set': cmd({ input: DesktopSettingsSchema.omit({ serverBaseUrl: true, mode: true }).partial(), output: DesktopSettingsSchema, scope: 'global', description: 'Update settings.' }),
  'settings.retentionInfo': cmd({ input: Empty, output: RetentionInfoSchema, scope: 'global', description: 'The distinct retention figures of spec section 7.' }),
  'settings.storageStatus': cmd({ input: Empty, output: StorageStatusSchema, scope: 'global', allowLocked: true, description: 'Local storage usage vs budget.' }),
  'settings.exportBackup': cmd({
    input: z.object({ destinationPath: z.string().optional(), includeSourceHistory: z.boolean().default(false), passphrase: z.string().min(12).max(1024) }),
    output: z.object({ path: z.string(), bytes: count, manifest: z.object({ createdAt: isoDateTime, includes: z.array(z.enum(['notes', 'settings', 'artifact_links', 'overrides', 'schedule', 'layout', 'source_history'])), accountCount: count, encrypted: z.literal(true), appVersion: SemVerSchema }) }),
    scope: 'global',
    description: 'Encrypted local backup; excludes secrets; never uploaded.',
  }),
  'settings.importBackup': cmd({
    input: z.object({ path: z.string(), passphrase: z.string().min(1).max(1024), mode: z.enum(['merge', 'replace']).default('merge') }),
    output: z.object({ imported: z.record(z.string(), count), warnings: z.array(z.string()) }),
    scope: 'global',
    description: 'Import a backup created by settings.exportBackup.',
  }),

  // -------------------------------------------------------------------------------------------
  // notifications change: local notification center (worker-owned; server merged by polling).
  // All global scope, no capability: the center is per signed-in user. `accountId` in payloads is
  // a filter or a tag, not the envelope scope.
  // -------------------------------------------------------------------------------------------
  'notifications.list': cmd({
    input: z.object({
      cursor: z.string().min(1).max(512).optional(),
      limit: z.number().int().min(1).max(200).default(100),
      unreadOnly: z.boolean().default(false),
      requiredOnly: z.boolean().default(false),
      accountId: AccountIdSchema.optional(),
      category: NotificationCategorySchema.optional(),
      level: NotificationLevelSchema.optional(),
      source: NotificationSourceSchema.optional(),
      includeDismissed: z.boolean().default(false),
    }),
    output: z.object({ items: z.array(LocalNotificationSchema), nextCursor: z.string().nullable(), unreadCount: count, requiredUnread: count }),
    scope: 'global',
    allowLocked: true,
    description: 'Local notifications newest first with filters; counts are for the whole center, not the page.',
  }),
  'notifications.markRead': cmd({ input: z.object({ ids: z.array(NotificationIdSchema).min(1).max(200) }), output: NotificationMutationResultSchema, scope: 'global', description: 'Mark notifications read; server-sourced ones are acknowledged upstream through the ack queue.' }),
  'notifications.markAllRead': cmd({ input: z.object({ accountId: AccountIdSchema.optional() }), output: NotificationMutationResultSchema, scope: 'global', description: 'Mark every unread notification read, optionally only one account (account-less notices count as every account).' }),
  'notifications.dismiss': cmd({ input: z.object({ ids: z.array(NotificationIdSchema).min(1).max(200) }), output: NotificationDismissResultSchema, scope: 'global', description: 'Hide notifications from the center. Required notices that are still unread are rejected with reason required_unread.' }),
  'notifications.clearRead': cmd({ input: Empty, output: NotificationMutationResultSchema, scope: 'global', description: 'Dismiss every read, undismissed notification.' }),
  'notifications.unreadCount': cmd({ input: Empty, output: NotificationCountsSchema, scope: 'global', allowLocked: true, description: 'Unread and required-unread counts (bell, dock badge).' }),
  'notifications.push': cmd({
    input: z.object({
      title: z.string().min(1).max(120),
      body: z.string().max(1000).default(''),
      level: NotificationLevelSchema,
      category: NotificationCategorySchema,
      accountId: AccountIdSchema.optional(),
      actions: z.array(NotificationActionSchema).max(4).default([]),
      dedupeKey: NotificationDedupeKeySchema.optional(),
    }),
    output: z.object({ notification: LocalNotificationSchema.nullable(), recorded: z.boolean() }),
    scope: 'global',
    description: 'Persist a renderer-originated notification (source desktop, never required). recorded=false when its category is muted; nothing is stored then.',
  }),
  'notifications.pullServer': cmd({ input: Empty, output: NotificationPullResultSchema, scope: 'global', description: 'Poll GET /v1/notifications?updatedAfter=<last> now and merge by server id.' }),
  'notifications.sendTest': cmd({ input: Empty, output: z.object({ id: NotificationIdSchema }), scope: 'global', localModeOnly: true, description: 'Local mode only: record a system notification that exercises toast, native notice and dock badge.' }),

  // -------------------------------------------------------------------------------------------
  // audit / migration / diagnostics
  // -------------------------------------------------------------------------------------------
  'audit.outboxStatus': cmd({ input: Empty, output: AuditOutboxStatusSchema, scope: 'global', allowLocked: true, description: 'Local audit outbox health.' }),
  'audit.flush': cmd({ input: Empty, output: z.object({ delivered: count, rejected: count, ackSequence: count.nullable(), faulted: z.boolean() }), scope: 'global', description: 'Deliver pending audit batches now.' }),
  'migration.inspect': cmd({ input: z.object({ folderPath: z.string().min(1) }), output: MigrationPlanSchema, scope: 'global', description: 'Inspect a prototype data folder and propose account mappings.' }),
  'migration.run': cmd({ input: z.object({ plan: z.object({ planDigest: z.string(), folderPath: z.string().min(1) }), mappings: z.array(MigrationMappingSchema) }), output: MigrationResultSchema, scope: 'global', description: 'Run the migration with a backup first; recoverable on interruption (A21).' }),
  'diagnostics.apiLog': cmd({ input: z.object({ runId: RunIdSchema, cursor: z.string().optional(), limit: z.number().int().min(1).max(1000).default(200) }), output: z.object({ items: z.array(ApiLogEntrySchema), nextCursor: z.string().nullable() }), scope: 'account', description: 'Request-level log for a run (metadata only).' }),
  'diagnostics.list': cmd({ input: z.object({ accountId: AccountIdSchema.optional(), from: isoDateTime.optional(), to: isoDateTime.optional(), level: DiagnosticEntrySchema.shape.level.optional(), limit: z.number().int().min(1).max(2000).default(500) }), output: z.object({ items: z.array(DiagnosticEntrySchema), retentionDays: z.literal(30) }), scope: 'either', allowLocked: true, description: 'Scrubbed local diagnostics.' }),
} as const;

export type Commands = typeof COMMANDS;
export type CommandName = keyof Commands;
export type CommandInput<N extends CommandName> = z.infer<Commands[N]['input']>;
export type CommandOutput<N extends CommandName> = z.infer<Commands[N]['output']>;

export const COMMAND_NAMES = Object.keys(COMMANDS) as CommandName[];
export const CommandNameSchema = z.enum(COMMAND_NAMES as [CommandName, ...CommandName[]]);

export function isCommandName(value: unknown): value is CommandName {
  return typeof value === 'string' && Object.hasOwn(COMMANDS, value);
}

export function getCommand<N extends CommandName>(name: N): Commands[N] {
  return COMMANDS[name];
}

/** Commands that may call engine.start() (A03). */
export const COLLECTION_COMMANDS = COMMAND_NAMES.filter((n) => (COMMANDS[n] as CommandDef).startsCollection === true);

// ---------------------------------------------------------------------------------------------
// Envelopes
// ---------------------------------------------------------------------------------------------

export const CommandEnvelopeSchema = z.object({
  command: z.string().min(1),
  operationId: OperationIdSchema,
  /** null for global commands. */
  accountId: AccountIdSchema.nullable(),
  payload: z.unknown(),
});
export type CommandEnvelope = z.infer<typeof CommandEnvelopeSchema>;

export type TypedCommandEnvelope<N extends CommandName> = {
  command: N;
  operationId: z.infer<typeof OperationIdSchema>;
  accountId: z.infer<typeof AccountIdSchema> | null;
  payload: CommandInput<N>;
};

export const IPC_ERROR_CODES = [
  'invalid_command',
  'invalid_payload',
  'invalid_envelope',
  'unauthenticated',
  'locked',
  'forbidden',
  'account_not_assigned',
  // local-accounts change: account-scoped command for an id that does not exist locally.
  'account_not_found',
  'account_required',
  'entitlement_expired',
  'recent_auth_required',
  'not_found',
  'conflict',
  'revision_mismatch',
  'rate_limited',
  'offline',
  'broker_unavailable',
  'worker_unavailable',
  'timeout',
  'cancelled',
  'unsupported',
  'external_failure',
  'outcome_unknown',
  'storage_full',
  'audit_outbox_faulted',
  // added by desktop-main fix pass (LH-03/F05): profile database needs recovery (A21).
  'recovery_required',
  'internal',
] as const;
export const IpcErrorCodeSchema = z.enum(IPC_ERROR_CODES);
export type IpcErrorCode = z.infer<typeof IpcErrorCodeSchema>;

export const IpcErrorSchema = z.object({
  code: IpcErrorCodeSchema,
  message: z.string(),
  operationId: OperationIdSchema.nullable(),
  /** Structured, allowlisted detail (zod issues, ids); never secrets or bodies. */
  details: z.record(z.string(), z.unknown()).optional(),
  retryAfterSeconds: z.number().int().nonnegative().optional(),
});
export type IpcError = z.infer<typeof IpcErrorSchema>;

export const CommandResultSchema = z.discriminatedUnion('ok', [
  z.object({ ok: z.literal(true), operationId: OperationIdSchema, result: z.unknown(), durationMs: count }),
  z.object({ ok: z.literal(false), operationId: OperationIdSchema, error: IpcErrorSchema }),
]);
export type CommandResult = z.infer<typeof CommandResultSchema>;
export type TypedCommandResult<N extends CommandName> =
  | { ok: true; operationId: string; result: CommandOutput<N>; durationMs: number }
  | { ok: false; operationId: string; error: IpcError };

export function parseCommandInput<N extends CommandName>(name: N, payload: unknown): CommandInput<N> {
  return COMMANDS[name].input.parse(payload) as CommandInput<N>;
}

export function safeParseCommandInput<N extends CommandName>(name: N, payload: unknown) {
  return COMMANDS[name].input.safeParse(payload) as z.ZodSafeParseResult<CommandInput<N>>;
}

/** Main-process channel names. */
export const IPC_CHANNELS = {
  command: 'agency:command',
  event: 'agency:event',
} as const;
